import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import AdminDashboard from './pages/admin/Dashboard';
import UserDashboard from './pages/user/Dashboard';
import Users from './pages/admin/Users';
import Departments from './pages/admin/Departments';
import UserGroups from './pages/admin/UserGroups';
import Conversations from './pages/admin/Conversations';
import Assistants from './pages/admin/Assistants';
import AssistantPermissions from './pages/admin/AssistantPermissions';
import OpenAIModels from './pages/admin/OpenAIModels';
import APIConfigurations from './pages/admin/APIConfigurations';
import News from './pages/admin/News';
import Terminal from './pages/admin/Terminal';
import TavilySearch from './pages/admin/TavilySearch';
import TavilySearchResults from './pages/admin/TavilySearchResults';
import TavilySearchConfig from './pages/admin/TavilySearchConfig';
import TavilyExtract from './pages/admin/TavilyExtract';
import TavilyExtractConfig from './pages/admin/TavilyExtractConfig';
import TavilyVectorization from './pages/admin/TavilyVectorization';
import VectorStore from './pages/admin/VectorStore';
import PDFFiles from './pages/admin/PDFFiles';
import Chunks from './pages/admin/Chunks';
import TiposEmbeddings from './pages/admin/TiposEmbeddings';
import Funcionalidades from './pages/admin/Funcionalidades';
import FuncionalidadesRoles from './pages/admin/FuncionalidadesRoles';
import FuncionalidadesGrupos from './pages/admin/FuncionalidadesGrupos';
import Empresas from './pages/admin/Empresas';
import EmpresaMensajesLimites from './pages/admin/EmpresaMensajesLimites';
import TiposSuscripcion from './pages/admin/TiposSuscripcion';
import Login from './pages/auth/Login';
import { useAuthStore } from './stores/authStore';

// Loading component for Suspense fallback
const LoadingSpinner = () => (
  <div className="h-full flex items-center justify-center bg-gray-100">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
      <p className="text-gray-600">Cargando...</p>
    </div>
  </div>
);

// Error boundary component
class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="h-full flex items-center justify-center bg-gray-100">
          <div className="text-center p-8 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold text-red-600 mb-4">¡Ups! Algo salió mal</h2>
            <p className="text-gray-600 mb-4">Ha ocurrido un error inesperado.</p>
            <button
              onClick={() => {
                // Clear any potentially corrupted auth state
                localStorage.removeItem('supabase.auth.token');
                localStorage.removeItem('auth-storage');
                this.setState({ hasError: false });
                window.location.href = '/login';
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Volver al inicio de sesión
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  const { user, isLoading, checkAuth } = useAuthStore();

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Clear any potentially invalid tokens before checking auth
        if (localStorage.getItem('supabase.auth.token') && 
            (!localStorage.getItem('auth-storage') || 
             localStorage.getItem('auth-storage') === 'undefined')) {
          localStorage.removeItem('supabase.auth.token');
          localStorage.removeItem('auth-storage');
        }
        
        await checkAuth();
      } catch (error) {
        console.error('Error checking auth:', error);
        // If there's an error with authentication, we'll redirect to login
        // This is handled in the checkAuth function
        
        // Clear any potentially corrupted auth state
        localStorage.removeItem('supabase.auth.token');
        localStorage.removeItem('auth-storage');
        window.location.href = '/login';
      }
    };
    
    initAuth();
  }, [checkAuth]);

  // Handle auth loading state
  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <ErrorBoundary>
      <Router>
        <Suspense fallback={<LoadingSpinner />}>
          <div className="h-full bg-gray-100 dark:bg-gray-900">
            <Routes>
              {/* Public Routes */}
              <Route 
                path="/login" 
                element={!user ? <Login /> : <Navigate to="/" replace />} 
              />
              
              {/* Protected Admin Routes */}
              <Route
                path="/admin/*"
                element={
                  user?.user_metadata?.role === 'administrador' ? (
                    <Layout>
                      <Routes>
                        <Route path="/" element={<AdminDashboard />} />
                        <Route path="/users" element={<Users />} />
                        <Route path="/departments" element={<Departments />} />
                        <Route path="/user-groups" element={<UserGroups />} />
                        <Route path="/conversations" element={<Conversations />} />
                        <Route path="/assistants" element={<Assistants />} />
                        <Route path="/assistant-permissions" element={<AssistantPermissions />} />
                        <Route path="/openai-models" element={<OpenAIModels />} />
                        <Route path="/api-configurations" element={<APIConfigurations />} />
                        <Route path="/news" element={<News />} />
                        <Route path="/terminal" element={<Terminal />} />
                        <Route path="/vector-store" element={<VectorStore />} />
                        <Route path="/pdf-files" element={<PDFFiles />} />
                        <Route path="/tavily-search" element={<TavilySearch />} />
                        <Route path="/tavily-search/results" element={<TavilySearchResults />} />
                        <Route path="/tavily-search/config" element={<TavilySearchConfig />} />
                        <Route path="/tavily-extract" element={<TavilyExtract />} />
                        <Route path="/tavily-extract/config" element={<TavilyExtractConfig />} />
                        <Route path="/tavily-vectorization" element={<TavilyVectorization />} />
                        <Route path="/chunks" element={<Chunks />} />
                        <Route path="/tipos-embeddings" element={<TiposEmbeddings />} />
                        <Route path="/funcionalidades" element={<Funcionalidades />} />
                        <Route path="/funcionalidades-roles" element={<FuncionalidadesRoles />} />
                        <Route path="/funcionalidades-grupos" element={<FuncionalidadesGrupos />} />
                        <Route path="/empresas" element={<Empresas />} />
                        <Route path="/empresa-mensajes-limites" element={<EmpresaMensajesLimites />} />
                        <Route path="/tipos-suscripcion" element={<TiposSuscripcion />} />
                      </Routes>
                    </Layout>
                  ) : (
                    <Navigate to="/" replace />
                  )
                }
              />

              {/* User Dashboard Route */}
              <Route
                path="/"
                element={
                  user ? (
                    user.user_metadata?.role === 'administrador' ? (
                      <Navigate to="/admin" replace />
                    ) : (
                      <UserDashboard />
                    )
                  ) : (
                    <Navigate to="/login" replace />
                  )
                }
              />
              
              {/* Catch all route */}
              <Route 
                path="*" 
                element={<Navigate to="/" replace />} 
              />
            </Routes>
          </div>
        </Suspense>
      </Router>
    </ErrorBoundary>
  );
}

export default App;